import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Navbar from './components/layout/Navbar';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';

import MetricSettings from './pages/MetricSettings';
import MetricSettingsForm from './pages/MetricSettings/MetricSettingsForm';
import BrandSettings from './pages/BrandSettings';
import BrandEditPage from './pages/BrandEditPage';
import DataEntry from './pages/DataEntry';
import Campaigns from './pages/Campaigns';
import MetricCodes from './pages/MetricCodes';
import MonthlyMetrics from './pages/MonthlyMetrics';
import MonthlyMetricForm from './pages/MonthlyMetrics/MonthlyMetricForm';
import AllBrandsMetrics from './pages/AllBrandsMetrics';
import MetricsSummary from './pages/MetricsSummary';
import EmbedPage from './pages/Embed';
import Users from './pages/Users';
import { useAuth } from './hooks/useAuth';
import './styles/index.css';
import MonthlyDataEntry from './pages/MonthlyDataEntry';
import CodeTypes from './pages/CodeTypes';
import AuditSettings from './pages/AuditSettings';
import Audits from './pages/Audits';
import HourlyReport from './pages/HourlyReport';
import DemoAudit from './pages/DemoAudit';
import Forecast from './pages/Forecast';
import ForecastForm from './pages/Forecast/ForecastForm';

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const { Content } = Layout;

const PrivateRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const EmbedLayout = ({ children }) => {
  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout>
        <Content style={{ background: '#fff', padding: 24, margin: 0, minHeight: 280 }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

const App = () => {
  const { isAuthenticated } = useAuth();

  // Embed route'u her zaman erişilebilir olmalı
  if (window.location.pathname === '/embed') {
    return (
      <EmbedLayout>
        <EmbedPage />
      </EmbedLayout>
    );
  }

  // Public routes
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }

  // Private routes
  return (
    <QueryClientProvider client={queryClient}>
      <Layout style={{ minHeight: '100vh' }}>
        <Navbar />
        <Layout>
          <Content style={{ background: '#fff', padding: 24, margin: 0, minHeight: 280 }}>
            <Routes>
              <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
              <Route path="/users" element={<PrivateRoute><Users /></PrivateRoute>} />
              <Route path="/metrics" element={<PrivateRoute><MetricSettings /></PrivateRoute>} />
              <Route path="/metrics/new" element={<PrivateRoute><MetricSettingsForm /></PrivateRoute>} />
              <Route path="/metrics/edit/:id" element={<PrivateRoute><MetricSettingsForm /></PrivateRoute>} />
              <Route path="/brands" element={<PrivateRoute><BrandSettings /></PrivateRoute>} />
              <Route path="/brands/new" element={<PrivateRoute><BrandEditPage /></PrivateRoute>} />
              <Route path="/brands/:id/edit" element={<PrivateRoute><BrandEditPage /></PrivateRoute>} />
              <Route path="/metric-codes" element={<PrivateRoute><MetricCodes /></PrivateRoute>} />
              <Route path="/data-entry" element={<PrivateRoute><DataEntry /></PrivateRoute>} />
              <Route path="/hourly-report" element={<PrivateRoute><HourlyReport /></PrivateRoute>} />
              <Route path="/campaigns" element={<PrivateRoute><Campaigns /></PrivateRoute>} />
              <Route path="/monthly-metrics" element={<PrivateRoute><MonthlyMetrics /></PrivateRoute>} />
              <Route path="/monthly-metrics/new" element={<PrivateRoute><MonthlyMetricForm /></PrivateRoute>} />
              <Route path="/monthly-metrics/edit/:id" element={<PrivateRoute><MonthlyMetricForm /></PrivateRoute>} />
              <Route path="/forecast" element={<PrivateRoute><Forecast /></PrivateRoute>} />
              <Route path="/forecast/new" element={<PrivateRoute><ForecastForm /></PrivateRoute>} />
              <Route path="/forecast/edit/:id" element={<PrivateRoute><ForecastForm /></PrivateRoute>} />
              <Route path="/all-brands-metrics" element={<PrivateRoute><AllBrandsMetrics /></PrivateRoute>} />
              <Route path="/metrics-summary" element={<PrivateRoute><MetricsSummary /></PrivateRoute>} />
              <Route path="/monthly-data-entry" element={<PrivateRoute><MonthlyDataEntry /></PrivateRoute>} />
              <Route path="/code-types" element={<PrivateRoute><CodeTypes /></PrivateRoute>} />
              <Route path="/audit-settings" element={<PrivateRoute><AuditSettings /></PrivateRoute>} />
              <Route path="/audits" element={<PrivateRoute><Audits /></PrivateRoute>} />
              <Route path="/demo-audit" element={<PrivateRoute><DemoAudit /></PrivateRoute>} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Content>
        </Layout>
      </Layout>
    </QueryClientProvider>
  );
};

export default App; 